import { CalendarDays, Clock, MapPin, Users, Lock, ExternalLink } from "lucide-react";
import { formatCHF, formatDate, formatTime } from "@/lib/api";
import { estComplet, placesTexte } from "@/components/EventCard";
import { LieuActions } from "@/components/LieuActions";
import { EventShare } from "@/components/EventShare";
import { EventCta } from "@/components/EventCta";

const Ligne = ({ icon: Icon, label, children, testid }) => (
  <div className="flex gap-3 py-3 border-b border-sand-dark last:border-0" data-testid={testid}>
    <Icon className="w-4 h-4 text-terra shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee">{label}</p>
      <div className="text-sm text-bark leading-relaxed">{children}</div>
    </div>
  </div>
);

export const EventSidebar = ({ event, config, onBook }) => {
  const complet = estComplet(event);
  const isVoyage = event.type === "voyage";
  const isSalon = event.type === "salon";
  const horaire = event.date_fin && event.date_fin.slice(0, 10) === event.date.slice(0, 10) ? `${formatTime(event.date)} – ${formatTime(event.date_fin)}` : formatTime(event.date);
  const rdvCache = !event.point_rdv && (event.rdv_prive !== false) && /communiqué|secret/i.test(event.lieu || "");
  const btn = "w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-sm tracking-wide transition-colors duration-300";
  return (
    <aside className="lg:sticky lg:top-28 bg-card border border-sand-dark rounded-2xl p-6 lg:p-8 space-y-6" data-testid="event-sidebar">
      {isSalon && event.statut_label && <p className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full border border-terra/60 text-terra inline-block" data-testid="event-statut">{event.statut_label}</p>}
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fumee mb-1">{isVoyage ? "Dès" : "Tarif"}</p>
          {event.prix != null ? <p className="font-serif text-4xl text-terra" data-testid="event-prix">{formatCHF(event.prix)}<span className="text-sm text-fumee font-sans"> / pers.</span></p>
            : <p className="font-mono text-xs uppercase tracking-[0.15em] text-sage" data-testid="event-prix">{event.reserve_parcours ? "Compris dans le Wyrd" : event.prix_label || (isSalon ? "Entrée libre" : "Tarif communiqué")}</p>}
          {event.location_tambour_prix ? <p className="text-xs text-fumee mt-1">+ {formatCHF(event.location_tambour_prix)} location d'un tambour</p> : null}
        </div>
        {!isVoyage && !isSalon && <p className="font-mono text-xs text-fumee flex items-center gap-1.5 shrink-0" data-testid="event-places"><Users className="w-3.5 h-3.5" />{event.annule || event.passe ? "—" : placesTexte(event)}</p>}
      </div>

      {isSalon && !event.lien_externe ? <p className="text-sm text-fumee text-center" data-testid="event-salon-libre">Venez nous rencontrer sur le stand — aucune inscription nécessaire.</p>
        : <EventCta event={event} config={config} onBook={onBook} className={btn} />}
      {!event.annule && !event.passe && !complet && !isVoyage && !isSalon && <p className="text-xs text-fumee text-center -mt-3">{event.reserve_parcours ? "Compris dans le parcours Le Wyrd — confirmation immédiate avec votre courriel de membre." : "Paiement en ligne (carte, TWINT) ou par facture. Confirmation immédiate par e-mail."}</p>}

      <div>
        <Ligne icon={CalendarDays} label="Date" testid="event-date"><span className={event.date_label ? "" : "capitalize"}>{event.date_label || formatDate(event.date)}</span></Ligne>
        {!event.date_label && <Ligne icon={Clock} label="Horaire" testid="event-horaire">{horaire}{event.duree ? <span className="text-fumee"> · {event.duree}</span> : null}</Ligne>}
        <Ligne icon={MapPin} label="Lieu" testid="event-lieu">
          {event.lieu}
          <LieuActions event={event} className="mt-2" />
        </Ligne>
        {event.point_rdv && <Ligne icon={MapPin} label="Point de rendez-vous" testid="event-point-rdv">
          <span className="block">{event.point_rdv}</span>
          {(event.rdv_lien || event.coordonnees) && <a href={event.rdv_lien || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.coordonnees)}`} target="_blank" rel="noopener noreferrer" data-testid="event-point-rdv-map-link" className="mt-2 inline-flex items-center gap-1.5 text-xs text-terra hover:text-earth transition-colors duration-300">
            Voir dans Google Maps <ExternalLink className="w-3.5 h-3.5" />
          </a>}
        </Ligne>}
        {rdvCache && <Ligne icon={Lock} label="Point de rendez-vous" testid="event-rdv-prive">Communiqué aux inscrit·e·s dans le courriel de confirmation.</Ligne>}
      </div>

      <EventShare event={event} />
    </aside>
  );
};
